/**
 * Painel /admin/queue — fila de geração de artes (TH-53262 / M3.E7.S1.2).
 *
 * Visível apenas para super_admin (item da sidebar tem `superAdminOnly`,
 * mas a página também aplica role guard inline para o caso do usuário
 * acessar a URL diretamente).
 *
 * Consome GET /api/arts/queue/status (TH-53261), com refetch a cada 30s
 * — `refetchIntervalInBackground` fica em default (false), então polling
 * pausa quando a aba sai de foco. Botão "Atualizar" no Topbar força refetch
 * imediato.
 *
 * Layout: grid 2×2 de KPIs (Pendentes / Em processamento / Concluídas hoje
 * / Falhadas hoje) + card "Tenants bloqueados" listando quem teve o
 * circuit breaker de IA aberto (ADR-019).
 */

import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface BlockedTenant {
  id: string;
  slug: string;
  name: string;
}

interface QueueStatusResponse {
  pending: number;
  in_progress: number;
  completed_today: number;
  failed_today: number;
  blocked_tenants: BlockedTenant[];
}

async function fetchQueueStatus(): Promise<QueueStatusResponse> {
  const res = await fetch("/api/arts/queue/status", {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as QueueStatusResponse;
}

export function QueueStatusPage() {
  return (
    <RequireAuth>
      <QueueStatusContent />
    </RequireAuth>
  );
}

function QueueStatusContent() {
  const { user, isLoading: authLoading } = useAuth();

  // Esperar /auth/me hidratar antes de decidir se nega acesso — caso
  // contrário um super_admin com aba recém-aberta veria "sem permissão"
  // por meio segundo até o cookie validar.
  if (authLoading) {
    return (
      <AppLayout
        breadcrumb="OPERAÇÃO / FILA DE IA"
        title="Fila de geração de artes"
      >
        <div className="text-sm text-[var(--color-muted-foreground)]">
          carregando…
        </div>
      </AppLayout>
    );
  }

  if (user?.role !== "super_admin") {
    return (
      <AppLayout
        breadcrumb="OPERAÇÃO / FILA DE IA"
        title="Fila de geração de artes"
      >
        <NotAuthorized />
      </AppLayout>
    );
  }

  return <QueueDashboard />;
}

function QueueDashboard() {
  const query = useQuery({
    queryKey: ["arts-queue-status"],
    queryFn: fetchQueueStatus,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const data = query.data;
  const isError = query.isError;

  const actions = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => query.refetch()}
      disabled={query.isFetching}
      aria-label="Atualizar"
      title="Atualizar"
    >
      <RefreshCw
        className={cn(
          "mr-2 h-4 w-4",
          query.isFetching ? "animate-spin" : null,
        )}
      />
      {query.isFetching ? "Atualizando…" : "Atualizar"}
    </Button>
  );

  return (
    <AppLayout
      breadcrumb="OPERAÇÃO / FILA DE IA"
      title="Fila de geração de artes"
      actions={actions}
    >
      <div className="flex flex-col gap-5">
        {isError ? (
          <div
            role="alert"
            className="rounded-lg border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
          >
            Não foi possível carregar a fila. O endpoint /api/arts/queue/status
            respondeu com erro — verifique se o worker está saudável.
          </div>
        ) : null}

        {/* Grid 2×2 de KPIs (sm:grid-cols-2 lg:grid-cols-4 segue padrão do Dashboard). */}
        <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            label="Pendentes"
            value={data ? String(data.pending) : "—"}
            hint="Enfileiradas, aguardando worker"
            tone={
              data && data.pending > 0
                ? "warn"
                : "muted"
            }
          />
          <KPICard
            label="Em processamento"
            value={data ? String(data.in_progress) : "—"}
            hint="Worker rodando IA→render"
            tone={
              data && data.in_progress > 0 ? "active" : "muted"
            }
            pulse={data ? data.in_progress > 0 : false}
          />
          <KPICard
            label="Concluídas hoje"
            value={data ? String(data.completed_today) : "—"}
            hint="Desde 00:00 UTC"
            tone="success"
          />
          <KPICard
            label="Falhadas hoje"
            value={data ? String(data.failed_today) : "—"}
            hint="Desde 00:00 UTC"
            tone={
              data && data.failed_today > 0 ? "destructive" : "muted"
            }
          />
        </div>

        {/* Tenants bloqueados — circuit breaker de IA (ADR-019). */}
        <BlockedTenantsCard
          tenants={data?.blocked_tenants ?? []}
          loading={query.isLoading}
        />
      </div>
    </AppLayout>
  );
}

interface KPICardProps {
  label: string;
  value: string;
  hint: string;
  tone: "muted" | "success" | "warn" | "destructive" | "active";
  pulse?: boolean;
}

function KPICard({ label, value, hint, tone, pulse }: KPICardProps) {
  const valueClass = cn(
    "mt-1.5 font-mono text-[26px] font-semibold tabular-nums tracking-[-0.02em]",
    tone === "warn" && "text-[var(--color-warning)]",
    tone === "destructive" && "text-[var(--color-destructive)]",
    tone === "success" && "text-[var(--color-success)]",
    tone === "active" && "text-[var(--color-primary)]",
  );
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-[18px] py-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
        {label}
      </div>
      <div className={valueClass}>
        {pulse ? (
          <span className="inline-flex items-center gap-2">
            <span
              aria-hidden
              className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--color-primary)]"
            />
            {value}
          </span>
        ) : (
          value
        )}
      </div>
      <div className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">
        {hint}
      </div>
    </div>
  );
}

interface BlockedTenantsCardProps {
  tenants: BlockedTenant[];
  loading: boolean;
}

function BlockedTenantsCard({ tenants, loading }: BlockedTenantsCardProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-[18px] py-[14px]">
        <div>
          <div className="text-sm font-semibold">Tenants bloqueados</div>
          <div className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">
            Circuit breaker de IA aberto (ADR-019)
          </div>
        </div>
        {tenants.length > 0 ? (
          <Badge variant="destructive">{tenants.length}</Badge>
        ) : null}
      </div>

      {loading ? (
        <div className="px-[18px] py-6 text-center text-sm text-[var(--color-muted-foreground)]">
          carregando…
        </div>
      ) : tenants.length === 0 ? (
        <div className="px-[18px] py-6 text-center text-sm text-[var(--color-muted-foreground)]">
          Nenhum tenant bloqueado.
        </div>
      ) : (
        <table className="w-full border-collapse text-xs">
          <thead className="bg-[var(--color-table-row-tint)]">
            <tr>
              <th className="px-[18px] py-[10px] text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                Nome
              </th>
              <th className="px-[18px] py-[10px] text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                Slug
              </th>
              <th className="px-[18px] py-[10px] text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                ID
              </th>
              <th className="px-[18px] py-[10px] text-right text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr
                key={tenant.id}
                className="border-t border-[var(--color-border)]"
              >
                <td className="px-[18px] py-[11px] text-sm">{tenant.name}</td>
                <td className="px-[18px] py-[11px] font-mono text-[11px] text-[var(--color-muted-foreground)]">
                  {tenant.slug}
                </td>
                <td className="px-[18px] py-[11px] font-mono text-[11px] text-[var(--color-muted-foreground)]">
                  {tenant.id.slice(0, 8)}
                </td>
                <td className="px-[18px] py-[11px] text-right">
                  <Badge variant="destructive">Bloqueado</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NotAuthorized() {
  return (
    <div className="mx-auto max-w-xl rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-8 text-center">
      <div className="text-base font-semibold">Acesso restrito</div>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
        Esta página é exclusiva para super administradores. Se você precisa de
        acesso, fale com o time responsável pela operação.
      </p>
    </div>
  );
}
